<template>
  <v-container>

    <v-alert
      prominent
      type="warning"
      icon="mdi-map-marker-question-outline"
      v-if="isAdmin"
    >
      <v-row align="center">
        <v-col class="grow">
          {{ $t("forms.info.warningDirectEdit") }}
        </v-col>
        <v-col class="shrink">
          <v-btn
            to="/manage/edit/publications"
          >
            <v-icon
              left
              dark
            >
              mdi-arrow-right-circle
            </v-icon>
            {{ $t("forms.info.gotoDirectEdit") }}
          </v-btn>
        </v-col>
      </v-row>
    </v-alert>

    <PendingUserForms
      v-if="pendingForms.length"
      :title="$t('table.publication.pendingForms')"
      :items="pendingForms"
    />

    <v-card class="mt-4">
      <v-card-title>{{ $t("forms.title") }}</v-card-title>
      <v-card-text>
        <v-list>
          <v-list-item-group>
            <v-row no-gutters>
              <v-col
                v-for="(form, index) in forms"
                :key="index"
                cols="12"
                md="6"
                lg="4"
                class="text-left"
              >
                <v-list-item :to="form.path">
                  <v-list-item-icon>
                    <v-icon v-text="form.icon"></v-icon>
                  </v-list-item-icon>
                  {{ $t(form.title) }}
                </v-list-item>
              </v-col>
            </v-row>
          </v-list-item-group>
        </v-list>
      </v-card-text>
    </v-card>

    <v-data-table
      :headers="headers"
      :items="computed_publications"
      :search="search"
      class="elevation-1 table-cursor-hand mt-4"
      :items-per-page="15"
      @click:row="clickRow"
      :footer-props="{
        itemsPerPageOptions: [15, 30, -1],
        itemsPerPageText: $t('table.itemsPerPageText')
      }"
      sort-by="display_date"
      sort-desc
      :loading="loading"
      :loading-text="$t('table.loading')"
    >
      <template v-slot:top>
        <v-toolbar flat color="white">
          <v-toolbar-title>{{ $t("table.all.publication") }}</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            :label="$t('search')"
            single-line
            hide-details
          ></v-text-field>
        </v-toolbar>
      </template>
      <template v-slot:item.minerva="{ item }">
        <div class="d-flex align-center">
          <v-tooltip top v-if="item.minerva_sync_status">
            <template v-slot:activator="{ on, attrs }">
              <v-chip
                small
                v-bind="attrs"
                v-on="on"
                class="minerva-chip"
                :color="item.minerva_status_color"
                :text-color="item.minerva_status_text_color"
              >
                {{ item.minerva_status_label }}
              </v-chip>
            </template>
            <div class="py-1">
              <span v-if="minervaStatusDescription(item.minerva_sync_status, item)">
                {{ minervaStatusDescription(item.minerva_sync_status, item) }}
              </span>
              <template v-if="item.minerva_sync_status !== 'wont-sync'">
                <span class="d-block" v-if="item.minerva_last_checked_at">
                  {{ $t('form.publication.minervaLastCheckedAt') }}:
                  {{ item.minerva_checked_display || formatMinervaLastCheckedAt(item.minerva_last_checked_at) }}
                </span>
                <span class="d-block" v-else>
                  {{ $t('form.publication.minerva.noChecks') }}
                </span>
              </template>
              <span class="d-block" v-if="item.minerva_sync_status === 'missing-data' && item.minerva_summary_text">
                {{ item.minerva_summary_text }}
              </span>
            </div>
          </v-tooltip>
          <v-btn
            v-if="availableMinervaUrl(item)"
            icon
            small
            class="ml-2"
            :href="availableMinervaUrl(item)"
            target="_blank"
            rel="noopener noreferrer"
            :aria-label="$t('form.publication.minerva.summary.available')"
          >
            <v-icon small>mdi-open-in-new</v-icon>
          </v-btn>
        </div>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import axios from "axios";
import PendingUserForms from "../forms/PendingUserForms";
import minervaDisplay from "@/mixins/minervaDisplay";
export default {
  mixins: [minervaDisplay],
  components: { PendingUserForms },
  data: () => ({
    pendingForms: [{}],
    dialog: false,
    loading: true,
    search: "",
    publications: [],
    forms: [
      {
        title: "publication.forms.newArticle",
        path: "/my-forms/publication/manual",
        icon: "mdi-plus"
      },
      {
        title: "publication.forms.newBookChapter",
        path: "/my-forms/publication/bookChapter",
        icon: "mdi-plus"
      },
      {
        title: "publication.forms.newCommunication",
        path: "/my-forms/publication/communication",
        icon: "mdi-plus"
      },
      {
        title: "publication.forms.newThesis",
        path: "/my-forms/publication/thesis",
        icon: "mdi-plus"
      }
    ]
  }),

  computed: {
    headers() {
      return [
        { text: this.$t("title.title"), value: "title" },
        { text: this.$t("form.authors"), value: "authors_text" },
        { text: this.$t("form.date"), value: "display_date" },
        { text: this.$t("table.minervaSync"), value: "minerva", sortable: false }
      ];
    },
    computed_publications() {
      return this.publications.map(part => {
        const item = { ...part };
        if (part.article != null){
          item.display_date = part.article.final_publication_date || part.article.online_publication_date || part.article.accepted_date;
        } else if (part.bookandchapter != null){
          item.display_date = part.bookandchapter.publication_date;
        } else if (part.communication != null){
          item.display_date = part.communication.date;
        } else if (part.thesis != null){
          item.display_date = part.thesis.lecture_date || part.thesis.deposit_date;
        } else {
          item.display_date = "Unknown";
        }
        const label = this.minervaStatusLabel(part.minerva_sync_status);
        const summary = this.minervaMetadataSummary(part);
        const checked = part.minerva_last_checked_at ? this.formatMinervaLastCheckedAt(part.minerva_last_checked_at) : "";
        item.minerva_status_label = label;
        item.minerva_summary_text = summary;
        item.minerva_checked_display = checked;
        item.minerva_status_color = this.minervaStatusColor(part.minerva_sync_status);
        item.minerva_status_text_color = this.minervaStatusTextColor(part.minerva_sync_status);
        item.minerva = [label, summary, checked].filter(Boolean).join(" ");
        return item;
      });
    }
  },

  created() {
    this.initialize();
  },

  methods: {
    initialize() {
      let path = this.$store.state.endpoint + "/user/publication/";
      axios
        .get(path)
        .then(response => {
          this.publications = response.data;
          this.loading = false;
        })
        .catch(error => {
          console.log(error);
        });
      path =
        this.$store.state.endpoint +
        "/user/moderatedOperations/pending/publication";
      axios
        .get(path)
        .then(response => {
          this.pendingForms = response.data;
        })
        .catch(error => {
          console.log(error);
        });
    },
    clickRow(item) {
      this.$router.push({ path: `/publications/${item.id}` });
    }
  }
};
</script>
